import graphqlClient from "@/GQLClient";
import {
  SaveActivityDocument,
  DeleteActivityDocument,
  type ActivityInput,
  GetInternalActivitiesOnMyInstitutionDocument,
  type ActivityFragment,
  GetSummarizedInternalActivitiesDocument,
  type SummarizedActivitiesFragment,
  Delete_Type,
  GetExternalActivitiesDocument,
  Module_Type,
  GetSummarizedExternalActivitiesDocument,
  type EntityPagerFilter,
  SaveActivityNotificationDocument,
  type NotificationSettingsInput,
} from "@/graphql/generated-types";
import SentryGraphQLError from "@/helpers/exceptions/SentryGraphQLError";
import type { IPagerResult } from "@/interfaces";
import type { IdType } from "@/types";

const getActivities = async (
  filter: EntityPagerFilter,
): Promise<IPagerResult<SummarizedActivitiesFragment>> => {
  const response = await graphqlClient.query({
    query: GetSummarizedInternalActivitiesDocument,
    variables: {
      filter,
    },
  });

  if (response.data.institutions?.getMyInstitution.activities) {
    const { edges, pageInfo } =
      response.data.institutions.getMyInstitution.activities;
    return {
      edges: edges.map((item) => item.node as SummarizedActivitiesFragment),
      pageInfo,
    };
  }

  throw new SentryGraphQLError();
};

const getActivityDetail = async (id: IdType) => {
  const ids: string[] = [];

  if (id) {
    ids.push(id.toString());
  }

  const response = await graphqlClient.query({
    query: GetInternalActivitiesOnMyInstitutionDocument,
    variables: {
      filter: {
        first: 1,
        ids,
      },
    },
  });

  if (response.data.institutions?.getMyInstitution.activities) {
    const { edges } = response.data.institutions.getMyInstitution.activities;
    return edges.map((item) => item.node as ActivityFragment).at(0);
  }

  throw new SentryGraphQLError();
};

const getExternalActivities = async (
  filter: EntityPagerFilter,
): Promise<IPagerResult<SummarizedActivitiesFragment>> => {
  const response = await graphqlClient.query({
    query: GetSummarizedExternalActivitiesDocument,
    variables: {
      filter,
      type: Module_Type.Activity,
    },
  });

  if (response.data.institutions?.getMyInstitution.externalContent) {
    const { edges, pageInfo } =
      response.data.institutions.getMyInstitution.externalContent;
    return {
      edges: edges.map((item) => item.node as SummarizedActivitiesFragment),
      pageInfo,
    };
  }

  throw new SentryGraphQLError();
};

const getExternalActivityDetail = async (id: IdType) => {
  const ids: string[] = [];

  if (id) {
    ids.push(id.toString());
  }

  const response = await graphqlClient.query({
    query: GetExternalActivitiesDocument,
    variables: {
      type: Module_Type.Activity,
      filter: {
        first: 1,
        ids,
      },
    },
  });

  if (response.data.institutions?.getMyInstitution.externalContent) {
    const { edges } =
      response.data.institutions.getMyInstitution.externalContent;
    return edges.map((item) => item.node as ActivityFragment).at(0);
  }

  throw new SentryGraphQLError();
};

const save = async (activity: ActivityInput) => {
  const response = await graphqlClient.mutate({
    mutation: SaveActivityDocument,
    variables: {
      activity,
    },
  });

  if (response.data?.activity?.save?.success) {
    return response;
  }

  throw new SentryGraphQLError();
};

const deleteItem = async (id: string, type: Delete_Type) => {
  const response = await graphqlClient.mutate({
    mutation: DeleteActivityDocument,
    variables: {
      id,
      type,
    },
  });

  if (response.data?.activity?.delete?.success) {
    return response;
  }

  throw new SentryGraphQLError();
};

const saveNotification = async (settings: NotificationSettingsInput) => {
  const response = await graphqlClient.mutate({
    mutation: SaveActivityNotificationDocument,
    variables: {
      settings,
    },
  });

  if (response.data?.activity?.saveNotificationSettings?.success) {
    return response.data.activity.saveNotificationSettings;
  }

  throw new SentryGraphQLError();
};

export default {
  getActivities,
  getActivityDetail,
  getExternalActivities,
  getExternalActivityDetail,
  save,
  deleteItem,
  saveNotification,
};
